import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminCollections() {
  const [cols, setCols] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/collections").then((r) => setCols(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const startEdit = (c) => setEditing({ ...c });
  const startNew = () => setEditing({ name: "", description: "", cover_image: "", order: cols.length + 1 });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB.");
    const r = new FileReader();
    r.onload = () => setEditing((p) => ({ ...p, cover_image: r.result }));
    r.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) {
        await api.put(`/admin/collections/${editing.id}`, {
          name: editing.name,
          description: editing.description,
          cover_image: editing.cover_image,
          order: Number(editing.order),
        });
      } else {
        await api.post("/admin/collections", {
          name: editing.name,
          description: editing.description,
          cover_image: editing.cover_image,
          order: Number(editing.order),
        });
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this collection?")) return;
    await api.delete(`/admin/collections/${id}`);
    load();
  };

  return (
    <div data-testid="admin-collections">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="eyebrow mb-3">Browsing</p>
          <h1 className="font-serif text-5xl font-light">Collections</h1>
        </div>
        <button onClick={startNew} className="btn-ink" data-testid="new-collection-button">+ New collection</button>
      </div>

      {loading ? <p className="eyebrow text-muted-foreground">Loading…</p> : (
        <div className="border-t border-border">
          {cols.map((c) => (
            <div key={c.id} className="grid grid-cols-12 gap-4 items-center py-5 border-b border-border" data-testid={`collection-row-${c.slug}`}>
              <div className="col-span-1 text-muted-foreground eyebrow">{String(c.order).padStart(2, "0")}</div>
              <div className="col-span-2 aspect-[4/3] bg-secondary">
                {c.cover_image && <img src={c.cover_image} alt={c.name} className="w-full h-full object-cover" />}
              </div>
              <div className="col-span-7">
                <p className="font-serif text-xl">{c.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{c.description}</p>
              </div>
              <div className="col-span-2 flex gap-4 justify-end text-sm">
                <button onClick={() => startEdit(c)} className="hair-link">Edit</button>
                <button onClick={() => del(c.id)} className="hair-link text-destructive">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-foreground/40 z-50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-background w-full max-w-lg p-10 border border-border" data-testid="collection-edit-dialog">
            <h2 className="font-serif text-3xl font-light mb-8">{editing.id ? "Edit collection" : "New collection"}</h2>
            <div className="space-y-5">
              <div>
                <label className="eyebrow mb-2 block">Name</label>
                <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-transparent border-b border-border py-3 outline-none" data-testid="collection-name" />
              </div>
              <div>
                <label className="eyebrow mb-2 block">Description</label>
                <textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="w-full bg-transparent border border-border p-3 outline-none" data-testid="collection-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow mb-2 block">Order</label>
                  <input type="number" value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="w-full bg-transparent border-b border-border py-3 outline-none" data-testid="collection-order" />
                </div>
                <div>
                  <label className="eyebrow mb-2 block">Cover image</label>
                  <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
                  <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline-ink w-full text-xs">{editing.cover_image ? "Replace" : "Upload"}</button>
                </div>
              </div>
              {editing.cover_image && <img src={editing.cover_image} alt="preview" className="w-full aspect-[4/3] object-cover" />}
            </div>
            <div className="flex gap-3 mt-8">
              <button type="button" onClick={() => setEditing(null)} className="btn-outline-ink">Cancel</button>
              <button type="submit" className="btn-ink flex-1" data-testid="collection-save">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
